// frontend/api/lokiLogger.ts

type LokiLogLevel = "debug" | "info" | "warning" | "error" | "critical";

export interface LokiLogLabels {
	[key: string]: string;
}

/**
 * LokiLogger: Logs messages to the Loki backend endpoint.
 *
 * For best results, pass file and line labels manually:
 *   LokiLogger.debug('message', { file: 'AssignmentForm.tsx', line: '123' });
 * This avoids the performance and reliability issues of stack parsing.
 */
export class LokiLogger {
	static async log(
		level: LokiLogLevel,
		message: string,
		labels: LokiLogLabels = {},
		accessToken?: string
	) {
		const moduleName = "frontend";
		let deviceId = "unknown";
		try {
			if (typeof window !== "undefined" && window.localStorage) {
				deviceId = window.localStorage.getItem("device_id") || "unknown";
			}
		} catch {}
		const mergedLabels = {
			module: moduleName,
			device_id: deviceId,
			...labels,
		};

		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};
		if (accessToken) {
			headers["Authorization"] = `Bearer ${accessToken}`;
		}

		try {
			const response = await fetch("/api/v2/loki-log/log/", {
				method: "POST",
				headers,
				body: JSON.stringify({
					message,
					level,
					labels: mergedLabels,
				}),
				credentials: accessToken ? "omit" : "same-origin",
			});
			if (!response.ok) {
				let errorMsg = `Loki log failed: ${response.status} ${response.statusText}`;
				try {
					const errorData = await response.json();
					errorMsg += ` | ${JSON.stringify(errorData)}`;
				} catch {}
				console.log(errorMsg);
			}
		} catch (error) {
			console.log("Failed to send log to Loki API:", error);
		}
	}

	static info(message: string, labels?: LokiLogLabels, accessToken?: string) {
		return this.log("info", message, labels, accessToken);
	}
	static debug(message: string, labels?: LokiLogLabels, accessToken?: string) {
		return this.log("debug", message, labels, accessToken);
	}
	static warning(message: string, labels?: LokiLogLabels, accessToken?: string) {
		return this.log("warning", message, labels, accessToken);
	}
	static error(message: string, labels?: LokiLogLabels, accessToken?: string) {
		return this.log("error", message, labels, accessToken);
	}
	static critical(message: string, labels?: LokiLogLabels, accessToken?: string) {
		return this.log("critical", message, labels, accessToken);
	}
}
